import React, { useEffect, useRef, useState, useCallback } from "react";
import { useField } from "@unform/core";
import { MdError } from "react-icons/md";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, ContentState, Modifier } from "draft-js";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import Tip from "../../components/Tip";
import { sanitizeWordHtml } from "./wordPasteHtml";

import { Container, Error, RootContainer, Label, AsteriskText } from "./styles";

const TextareaWithLabelAndEditor = ({
  name,
  containerStyle = {},
  icon: Icon,
  validationBlur,
  label,
  tip,
  tip2,
  className,
  disabled,
  readOnly,
  required,
  marginBottom1,
  marginBottom2,
  customFontSizeOptions = [
    8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 24, 26, 28, 30,
    32, 34, 36, 40, 44, 48, 54, 60, 66, 72, 80, 88, 96,
  ],
  ...rest
}) => {
  const inputRef = useRef(null);

  const [isFocused, setIsFocused] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const { fieldName, defaultValue, error, registerField } = useField(name);

  const [editorState, setEditorState] = useState(() => {
    const processedValue = !defaultValue ? "" : defaultValue;
    const contentBlock = htmlToDraft(processedValue);
    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(
        contentBlock.contentBlocks
      );
      return EditorState.createWithContent(contentState);
    } else return EditorState.createEmpty();
  });

  const handleInputFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleInputBlur = useCallback(async () => {
    let fieldHasValue = !!inputRef.current?.value;
    setIsFocused(false);

    setIsFilled(fieldHasValue);

    if (validationBlur) {
      validationBlur(fieldName, inputRef.current?.value);
    }
  }, [validationBlur, fieldName]);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: "value",
    });
  }, [fieldName, registerField]);

  const removeBlankString = (string) => {
    if (string.replace(/(<p[^>]+?>|<p>|<\/p>)/gim, "") === "\n") return "";
    return string;
  };

  const handlePastedText = useCallback(
    (text, html) => {
      if (disabled || readOnly || !html) return false;

      try {
        const draft = htmlToDraft(
          sanitizeWordHtml(html, customFontSizeOptions)
        );
        if (!draft || draft.contentBlocks.length === 0) return false;

        const pastedContent = ContentState.createFromBlockArray(
          draft.contentBlocks,
          draft.entityMap
        );

        const currentContent = editorState.getCurrentContent();
        const selection = editorState.getSelection();
        const newContent = Modifier.replaceWithFragment(
          currentContent,
          selection,
          pastedContent.getBlockMap()
        );

        setEditorState(
          EditorState.forceSelection(
            EditorState.push(editorState, newContent, "insert-fragment"),
            newContent.getSelectionAfter()
          )
        );
        return true;
      } catch (err) {
        return false;
      }
    },
    [disabled, readOnly, editorState, customFontSizeOptions]
  );

  return (
    <RootContainer className={className}>
      <Label marginBottom={marginBottom1}>
        {label}
        {required && <AsteriskText>*{` `}</AsteriskText>}
        {tip && <Tip>({tip})</Tip>}
      </Label>
      <Label marginBottom={marginBottom2}>{tip2 && <Tip>{tip2}</Tip>}</Label>
      <Container
        style={containerStyle}
        isFocused={isFocused}
        isFilled={isFilled}
        isErrored={!!error}
        disabled={disabled}
        data-testid="input-container"
      >
        <Editor
          disabled={disabled || readOnly}
          readOnly={readOnly || disabled}
          wrapperClassName={"editor-input"}
          editorState={editorState}
          onEditorStateChange={(newState) => {
            if (!disabled && !readOnly) {
              setEditorState(newState);
            }
          }}
          handlePastedText={handlePastedText}
          toolbar={{
            inline: {
              superscript: { className: "no-display" },
              subscript: { className: "no-display" },
              monospace: { className: "no-display" },
            },
            fontSize: {
              options: customFontSizeOptions,
            },
            list: { inDropdown: true },
            textAlign: { className: "no-display" },
            history: { className: "no-display" },
            fontFamily: { className: "no-display" },
            emoji: { className: "no-display" },
            embedded: { className: "no-display" },
            remove: { className: "no-display" },
            blockType: { className: "no-display" },
          }}
        />
        <textarea
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          ref={inputRef}
          disabled={disabled || readOnly}
          readOnly={readOnly || disabled}
          value={removeBlankString(
            draftToHtml(convertToRaw(editorState.getCurrentContent()))
          )}
          {...rest}
        />
      </Container>
      {error && (
        <Error title={error}>
          <MdError color="#e00000" size={20} />
          {error}
        </Error>
      )}
    </RootContainer>
  );
};

export default TextareaWithLabelAndEditor;
